import { useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Plus, Search, Loader2, ChevronLeft, ChevronRight, ArrowLeft, Route as RouteIcon,
  Calendar, Building2, Truck, UserCog, Lock, ScanLine, Trash2,
  FileText, Fuel, AlertTriangle, FilterX, List as ListIcon,
  ArrowUp, ArrowDown, ArrowUpDown, Pencil } from 'lucide-react';
import { UploadStatusRow } from '@/components/common/UploadStatusRow';
import { DeleteConfirmDrawer } from '@/components/common/DeleteConfirmDrawer';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/Sheet';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useTrips } from '@/hooks/useTrips';
import { useSites } from '@/hooks/useSites';
import { useVehicles } from '@/hooks/useVehicles';
import { useDrivers } from '@/hooks/useDrivers';
import { useIsAdmin } from '@/lib/permissions';
import { QuickLogForm } from './QuickLogForm';
import { ExcelToolbar } from '@/components/excel/ExcelToolbar';
import { TripDetailSheet } from './TripDetailSheet';
import { LockMonthDialog } from '@/components/trips/LockMonthDialog';
import { TripStatusBadge } from '@/components/trips/TripStatusBadge';
import { TripCalendar } from '@/components/trips/TripCalendar';
import { TripDayDrillSheet } from '@/components/trips/TripDayDrillSheet';
import { formatSar } from '@/lib/trip-math';
import { formatTripDate } from '@/lib/format';
import type { Trip, TripStatus, TripListParams } from '@/lib/types';
import { useAuth } from '@/lib/auth';

type DateFilter = 'today' | 'week' | 'month' | 'all' | 'custom';
type StatusFilter = 'all' | TripStatus;
type SortKey = NonNullable<TripListParams['sort']>;

const STATUS_FILTERS: StatusFilter[] = [
  'all', 'draft', 'confirmed', 'invoiced', 'paid', 'disputed', 'cancelled',
];

function dateRangeFor(filter: DateFilter): { dateFrom?: string; dateTo?: string } {
  const today = new Date();
  const isoDate = (d: Date) => d.toISOString().slice(0, 10);

  if (filter === 'all' || filter === 'custom') return {};
  if (filter === 'today') {
    const todayIso = isoDate(today);
    return { dateFrom: todayIso, dateTo: todayIso };
  }
  if (filter === 'week') {
    const start = new Date(today); start.setDate(today.getDate() - 6);
    return { dateFrom: isoDate(start), dateTo: isoDate(today) };
  }
  // month
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return { dateFrom: isoDate(start), dateTo: isoDate(today) };
}

export default function TripsPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // PR-MAY15-LEDGERS-V3 item 1 — default to "week" not "today" because
  // operators routinely log trips backdated by 1-5 days (a driver hauls
  // 7 trips and logs them the next morning). "Today" returns 0 → user
  // believes the trip didn't save; "Week" surfaces every recent backdate.
  const [dateFilter, setDateFilter] = useState<DateFilter>('week');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // PR-CHIPS MAY 11 — ?missing= filter: empty string = no filter; otherwise
  // one of the photo kinds, or 'any' for any required-and-missing chip.
  const [missingFilter, setMissingFilter] = useState<'' | 'delivery_slip' | 'fuel_receipt' | 'any'>('');
  // PR-MAY19-TRIPS-LIST-OVERHAUL — combinable per-entity filters (vehicle,
  // site, driver) and an explicit custom date range. Empty string = no
  // filter. The day-drill panel uses these to "jump into the list filtered
  // by this vehicle on this day" so a flagged vehicle is one click from
  // its individual trip rows.
  const [vehicleFilter, setVehicleFilter] = useState<string>('');
  const [siteFilter,    setSiteFilter]    = useState<string>('');
  const [driverFilter,  setDriverFilter]  = useState<string>('');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo,   setCustomTo]   = useState<string>('');

  // The period on screen, at component level so both the query below and the
  // Export button can read it. It was computed inside the query's useMemo,
  // which put it out of reach of anything that renders.
  const exportRange = dateFilter === 'custom'
    ? { dateFrom: customFrom || undefined, dateTo: customTo || undefined }
    : dateRangeFor(dateFilter);
  // Column sort. Undefined = legacy tripDate-desc + createdAt-desc tuple
  // (preserves existing pager/calendar behaviour).
  const [sortKey, setSortKey] = useState<SortKey | undefined>(undefined);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); setPage(1); return; }
    if (sortDir === 'asc') { setSortDir('desc'); setPage(1); return; }
    setSortKey(undefined); setSortDir('desc'); setPage(1);
  };
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [lockingMonth, setLockingMonth] = useState(false);
  // PR-DEL-TRIPS-DRIVERS MAY 11 — bulk delete state (super_admin only).
  // PR-PERMS-CLIENTS MAY 12 — admin can delete (no-force).
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const qcTrips = useQueryClient();
  const toggleSelect = (id: string) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  async function performBulkDelete({ force, reason }: { force: boolean; reason: string }) {
    await apiClient.post('/api/v1/trips/bulk-delete', {
      ids: Array.from(selected),
      reason,
      force,
    });
    setSelected(new Set());
  }
  const isAdmin = useIsAdmin();
  const [viewing, setViewing] = useState<Trip | null>(null);
  /** How many rows at a time. A dispatcher checking a day wants 25; someone
   *  reconciling a month wants 100 and no clicking. */
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // PR-MAY14-TRIP-CALENDAR — view toggle. Default = list; calendar view
  // re-fetches with a wide date window so the month grid has data.
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  // PR-MAY19-CALENDAR-DAYDRILL — selected calendar day for the drill-down
  // panel; null = closed. Set by TripCalendar's onPickDay.
  const [drillDate, setDrillDate] = useState<string | null>(null);

  useEffect(() => {
    const tm = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(tm);
  }, [searchInput]);

  const params: TripListParams = useMemo(() => {
    if (viewMode === 'calendar') {
      // Pull every trip in the visible month + flanking week pad so the
      // calendar grid never shows a blank "today" the user knows has trips.
      const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
      const last  = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
      first.setDate(first.getDate() - 7);
      last.setDate(last.getDate() + 7);
      const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      return {
        search: search || undefined,
        dateFrom: ymd(first), dateTo: ymd(last),
        status: statusFilter === 'all' ? undefined : statusFilter,
        page: 1, pageSize: 500,
      };
    }
    const range = dateFilter === 'custom'
      ? { dateFrom: customFrom || undefined, dateTo: customTo || undefined }
      : dateRangeFor(dateFilter);
    return {
      search: search || undefined,
      ...range,
      vehicleId: vehicleFilter || undefined,
      siteId:    siteFilter    || undefined,
      driverId:  driverFilter  || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      missing: missingFilter || undefined,
      sort:  sortKey,
      order: sortKey ? sortDir : undefined,
      page, pageSize: rowsPerPage,
    };
  }, [search, dateFilter, customFrom, customTo, statusFilter, missingFilter,
      vehicleFilter, siteFilter, driverFilter, sortKey, sortDir,
      page, viewMode, calendarMonth]);

  const { data, isLoading, isError, error, refetch } = useTrips(params);

  // Unfiltered total — used for the "847 trips · filtered: 60" caption.
  // Kept to dateFilter=all so the headline number is the absolute count
  // (matches the user's mental model: "I have 847 trips total").
  const unfilteredTotal = useTrips({ pageSize: 1, page: 1 }).data?.total ?? 0;

  // Dropdown data sources. pageSize=300+ caps that the corresponding list
  // endpoints already accept (see useDrivers spec). Disabled when the
  // filter dropdowns aren't on screen (calendar mode) to keep the page
  // fast on the calendar-only flow.
  const sitesQ    = useSites({    pageSize: 200 });
  const vehiclesQ = useVehicles({ pageSize: 500 });
  const driversQ  = useDrivers({  pageSize: 300 });
  const siteOptions    = sitesQ.data?.items    ?? [];
  const vehicleOptions = vehiclesQ.data?.items ?? [];
  const driverOptions  = driversQ.data?.items  ?? [];

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // PR-MAY19-TRIPS-LIST-OVERHAUL — has-any-filter detector for the Clear
  // button. The default ("week", status=all, no docs, no entity, no
  // search) doesn't count as "filtered".
  const anyFilter =
    !!search || dateFilter !== 'week' || statusFilter !== 'all' ||
    !!missingFilter || !!vehicleFilter || !!siteFilter || !!driverFilter;
  const clearAll = () => {
    setSearchInput(''); setSearch('');
    setDateFilter('week'); setCustomFrom(''); setCustomTo('');
    setStatusFilter('all'); setMissingFilter('');
    setVehicleFilter(''); setSiteFilter(''); setDriverFilter('');
    setSortKey(undefined); setSortDir('desc');
    setPage(1);
  };

  const displayName = user?.fullNameEn ?? user?.fullName ?? '';

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher /><ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout}>{t('auth.logout')}</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <span className="text-xs text-muted-foreground">{displayName}</span>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-3">
            <RouteIcon className="h-7 w-7 text-teal-500" />
            <h1 className="font-display text-3xl text-foreground">{t('trips.title')}</h1>
            <span className="text-sm text-muted-foreground">({total})</span>
          </div>
          {isAdmin && (
          <Button variant="outline" onClick={() => setLockingMonth(true)} className="gap-1.5">
            <Lock className="h-4 w-4" /> {t('closings.lockMonth.button')}
          </Button>
        )}
        {isSuperAdmin && selected.size > 0 && (
          <Button
            variant="default"
            onClick={() => setDeleteOpen(true)}
            className="gap-1.5 bg-rose-600 hover:bg-rose-700 text-white"
          >
            <Trash2 className="h-4 w-4" /> Delete selected ({selected.size})
          </Button>
        )}
        <Button onClick={() => setCreating(true)} className="gap-1.5">
            <Plus className="h-4 w-4" /> {t('trips.quickLog')}
          </Button>
        </div>

        {/* Bulk Excel: download the sheet, fill a whole month of trips, upload back */}
        <div className="mb-6">
          {/* Export follows the date filter on screen: choosing a month and
              pressing Export downloads that month, not the whole history. */}
          <ExcelToolbar
            entity="trips"
            title="trips"
            /* Trips carry a side, so the upload has to ask for one. Without
               this the toolbar sent no project and every imported trip landed
               invisible on a page that filters by side. */
            askProject
            onImported={() => { void refetch(); }}
            exportFrom={exportRange.dateFrom}
            exportTo={exportRange.dateTo}
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {/* PR-MAY14-TRIP-CALENDAR — view-mode toggle (list vs calendar). */}
          <div className="inline-flex rounded-md border border-border bg-card overflow-hidden" data-trip-viewmode-toggle>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-sm inline-flex items-center gap-1 transition-colors ${
                viewMode === 'list' ? 'bg-gold/20 text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
              }`}
              data-trip-viewmode="list"
            >
              <ListIcon className="h-3.5 w-3.5" /> List
            </button>
            <button
              type="button"
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 text-sm inline-flex items-center gap-1 transition-colors ${
                viewMode === 'calendar' ? 'bg-gold/20 text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
              }`}
              data-trip-viewmode="calendar"
            >
              <Calendar className="h-3.5 w-3.5" /> Calendar
            </button>
          </div>

          {/* Date filter chips — only relevant in list mode. */}
          {viewMode === 'list' && (
          <div className="inline-flex rounded-md border border-border bg-card overflow-hidden" data-trips-date-presets>
            {(['today', 'week', 'month', 'all'] as DateFilter[]).map((d) => (
              <button
                key={d}
                onClick={() => { setDateFilter(d); setPage(1); }}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  dateFilter === d
                    ? 'bg-gold/20 text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
                data-trips-date-preset={d}
              >
                {t(`trips.dateFilter.${d}`)}
              </button>
            ))}
            <button
              onClick={() => { setDateFilter('custom'); setPage(1); }}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                dateFilter === 'custom'
                  ? 'bg-gold/20 text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
              }`}
              title="Pick a custom date range"
            >
              Custom
            </button>
          </div>
          )}

          {/* PR-MAY19-TRIPS-LIST-OVERHAUL — custom range pickers (only when
              the "Custom" preset is active so the bar stays compact). */}
          {viewMode === 'list' && dateFilter === 'custom' && (
            <div className="inline-flex items-center gap-1 text-xs" data-trips-custom-range>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => { setCustomFrom(e.target.value); setPage(1); }}
                className="h-9 px-2 rounded border border-border bg-background"
                data-trips-custom-from
              />
              <span className="text-muted-foreground">→</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => { setCustomTo(e.target.value); setPage(1); }}
                className="h-9 px-2 rounded border border-border bg-background"
                data-trips-custom-to
              />
            </div>
          )}

          {/* PR-MAY19-TRIPS-LIST-OVERHAUL — combinable entity filters. */}
          {viewMode === 'list' && (
            <>
              <select
                value={vehicleFilter}
                onChange={(e) => { setVehicleFilter(e.target.value); setPage(1); }}
                className="h-9 px-3 rounded border border-border bg-background text-sm max-w-[180px]"
                title="Filter by vehicle"
                data-trips-vehicle-filter
              >
                <option value="">All vehicles</option>
                {vehicleOptions.map((v) => (
                  <option key={v.id} value={v.id}>{v.plateEn ?? v.code}</option>
                ))}
              </select>
              <select
                value={siteFilter}
                onChange={(e) => { setSiteFilter(e.target.value); setPage(1); }}
                className="h-9 px-3 rounded border border-border bg-background text-sm max-w-[180px]"
                title="Filter by site"
                data-trips-site-filter
              >
                <option value="">All sites</option>
                {siteOptions.map((s) => (
                  <option key={s.id} value={s.id}>{s.nameEn ?? s.code}</option>
                ))}
              </select>
              <select
                value={driverFilter}
                onChange={(e) => { setDriverFilter(e.target.value); setPage(1); }}
                className="h-9 px-3 rounded border border-border bg-background text-sm max-w-[180px]"
                title="Filter by driver"
                data-trips-driver-filter
              >
                <option value="">All drivers</option>
                {driverOptions.map((d) => (
                  <option key={d.id} value={d.id}>{d.fullNameEn ?? d.code}</option>
                ))}
              </select>
            </>
          )}

          {/* Status filter dropdown */}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setPage(1); }}
            className="h-9 px-3 rounded border border-border bg-background text-sm"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>{t(`trips.statusFilter.${s}`)}</option>
            ))}
          </select>

          {/* PR-CHIPS MAY 11 — Missing-doc filter */}
          <select
            value={missingFilter}
            onChange={(e) => { setMissingFilter(e.target.value as typeof missingFilter); setPage(1); }}
            className="h-9 px-3 rounded border border-border bg-background text-sm"
            title="Filter trips by missing required documents"
          >
            <option value="">All docs</option>
            <option value="any">Missing any required doc</option>
            <option value="delivery_slip">Missing delivery slip</option>
            <option value="fuel_receipt">Missing fuel receipt</option>
          </select>

          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Code, plate, or driver name"
              className="ps-8 h-9"
              data-trips-search
            />
          </div>

          {/* PR-MAY19-TRIPS-LIST-OVERHAUL — single Clear-all button (replaces
              the per-filter clears). Only visible when something is active. */}
          {anyFilter && (
            <Button variant="ghost" size="sm" className="gap-1" onClick={clearAll} data-trips-clear-filters>
              <FilterX className="h-3.5 w-3.5" /> Clear filters
            </Button>
          )}
        </div>

        {/* PR-MAY19-TRIPS-LIST-OVERHAUL — filtered-count caption. Renders
            even when nothing is filtered so the operator always knows the
            absolute denominator (847 of 847 trips). */}
        {viewMode === 'list' && (
          <div className="text-xs text-muted-foreground mb-3" data-trips-count>
            {unfilteredTotal} trips total{total !== unfilteredTotal ? ` · filtered: ${total}` : ''}
          </div>
        )}

        {/* PR-MAY14-TRIP-CALENDAR — calendar branch lives ahead of the
            empty/error/list branches so it always renders when toggled,
            even if the month happens to have zero trips. */}
        {viewMode === 'calendar' ? (
          <TripCalendar
            trips={items as unknown as { id: string; code: string; tripDate: string; client?: { nameEn?: string | null } | null; status: string }[]}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            onPickDay={(ymd) => setDrillDate(ymd)}
            onPickTrip={(id) => {
              const t = items.find((x) => x.id === id);
              if (t) setViewing(t);
            }}
          />
        ) : (
        <>
        {/* List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="rounded border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {t('common.errorLoading')}: {String((error as Error)?.message ?? error)}
            <Button variant="outline" size="sm" className="ms-3" onClick={() => refetch()}>{t('common.retry')}</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/10 py-16 text-center">
            <RouteIcon className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <h3 className="font-medium text-foreground mb-1">{t('trips.empty.title')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t('trips.empty.description')}</p>
            <Button onClick={() => setCreating(true)} className="gap-1.5">
              <Plus className="h-4 w-4" /> {t('trips.quickLog')}
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 border-b border-border sticky top-0 z-10
                                text-[11px] uppercase tracking-wider shadow-sm">
                <tr className="text-start">
                  {isSuperAdmin && (
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-rose-600"
                        onChange={(e) => {
                          if (e.target.checked) setSelected(new Set(items.map((tr) => tr.id)));
                          else setSelected(new Set());
                        }}
                        checked={items.length > 0 && selected.size === items.length}
                      />
                    </th>
                  )}
                  <SortableTh label={t('trips.col.code')}    sortKey="code"         currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                  <SortableTh label={t('trips.col.date')}    sortKey="tripDate"     currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} extraClass="hidden sm:table-cell" />
                  <SortableTh label={t('trips.col.site')}    sortKey="site"         currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                  <th className="px-3 py-2 text-start font-medium hidden lg:table-cell">Client</th>
                  <SortableTh label={t('trips.col.vehicle')} sortKey="vehicle"      currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} extraClass="hidden md:table-cell" />
                  <SortableTh label={t('trips.col.driver')}  sortKey="driver"       currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} extraClass="hidden lg:table-cell" />
                  <SortableTh label={t('trips.col.qty')}     sortKey="deliveredQty" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="end" />
                  {isAdmin && <SortableTh label={t('trips.col.companyKeep')} sortKey="companyKeep" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="end" extraClass="hidden md:table-cell" />}
                  <th className="text-start px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground hidden md:table-cell">DOCS</th>
                  <SortableTh label={t('trips.col.status')} sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="end" />

                  <th className="px-3 py-2 w-16" />
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // PR-MAY19-TRIPS-LIST-OVERHAUL — date partition headers.
                  // Only insert when the data is grouped by date (default
                  // sort, or explicit sort=tripDate). Counts within the
                  // current PAGE only — the global per-day totals come
                  // from the calendar drill-down endpoint.
                  const partitionsOn = sortKey === undefined || sortKey === 'tripDate';
                  const colSpan = 1 + 8 + (isSuperAdmin ? 1 : 0) + (isAdmin ? 1 : 0); // safe upper bound
                  const groupsByDate = new Map<string, number>();
                  if (partitionsOn) {
                    for (const trip of items) {
                      const key = String(trip.tripDate).slice(0, 10);
                      groupsByDate.set(key, (groupsByDate.get(key) ?? 0) + 1);
                    }
                  }
                  const rows: ReactNode[] = [];
                  let lastDateKey: string | null = null;
                  for (const trip of items) {
                    const key = String(trip.tripDate).slice(0, 10);
                    if (partitionsOn && key !== lastDateKey) {
                      rows.push(
                        <tr key={`part-${key}`} className="bg-muted/40 border-y border-border" data-trips-date-partition={key}>
                          <td colSpan={colSpan} className="px-3 py-2 text-xs font-semibold text-foreground">
                            {new Date(trip.tripDate).toLocaleDateString('en-GB',
                              { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                            <span className="font-normal text-muted-foreground">
                              {' · '}{groupsByDate.get(key)} trip{groupsByDate.get(key) === 1 ? '' : 's'}
                              {(() => {
                                // The volume of the day, from the rows on screen.
                                const vol = items
                                  .filter((x) => String(x.tripDate).slice(0, 10) === key)
                                  .reduce((a, x) => a + Number(x.deliveredQty ?? 0), 0);
                                return vol ? ` · ${vol.toLocaleString('en-US', { maximumFractionDigits: 2 })} delivered` : '';
                              })()}
                            </span>
                          </td>
                        </tr>,
                      );
                      lastDateKey = key;
                    }
                    rows.push(
                      <tr
                        key={trip.id}
                        onClick={() => setViewing(trip)}
                        className="border-b border-border last:border-b-0 hover:bg-muted/20 cursor-pointer transition-colors"
                        data-trip-row={trip.id}
                      >
                        {isSuperAdmin && (
                          <td className="px-3 py-2 w-8" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-rose-600"
                              checked={selected.has(trip.id)}
                              onChange={() => toggleSelect(trip.id)}
                            />
                          </td>
                        )}
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="font-mono text-xs rounded-md bg-muted px-2 py-1 text-foreground">
                            {trip.code}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground hidden sm:table-cell whitespace-nowrap" data-trip-date>{formatTripDate(trip.tripDate)}</td>
                        <td className="px-3 py-2">
                          <div className="whitespace-nowrap">{trip.site?.nameEn ?? '?'}</div>
                          <div className="text-xs text-muted-foreground">{t(`trips.tripType.${trip.tripType}`)}</div>
                        </td>
                        {/* Both already ride down with every row - the table
                            simply never showed them. */}
                        <td className="px-3 py-2 hidden lg:table-cell whitespace-nowrap">{trip.client?.nameEn ?? '—'}</td>
                        <td className="px-3 py-2 hidden md:table-cell whitespace-nowrap">
                          {trip.vehicle?.plateEn
                            ? <span className="font-mono text-xs font-semibold rounded-md border border-border px-2 py-1">
                                {trip.vehicle.plateEn}
                              </span>
                            : <span className="text-muted-foreground">?</span>}
                        </td>
                        <td className="px-3 py-2 hidden lg:table-cell">
                          {trip.driver?.fullNameEn ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="h-6 w-6 shrink-0 rounded-full bg-muted text-[10px] font-medium
                                               flex items-center justify-center text-muted-foreground">
                                {trip.driver.fullNameEn.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
                              </span>
                              <span className="whitespace-nowrap">{trip.driver.fullNameEn}</span>
                            </span>
                          ) : <span className="text-muted-foreground">?</span>}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums whitespace-nowrap">
                          {Number(trip.deliveredQty)}
                          <span className="text-xs text-muted-foreground"> / {Number(trip.targetQty)}</span>
                        </td>
                        {isAdmin && (
                          <td className="px-3 py-2 text-end tabular-nums hidden md:table-cell">
                            {formatSar(Number(trip.companyKeepNetSar ?? trip.companyKeep))}
                          </td>
                        )}
                        <td className="px-3 py-2 hidden md:table-cell" onClick={(e) => e.stopPropagation()}>
                          {trip.uploadStatus && (
                            <UploadStatusRow chips={[
                              { status: trip.uploadStatus.deliverySlip,   icon: FileText,       label: 'Delivery slip',
                                onClick: () => setViewing({ ...trip }) },
                              { status: trip.uploadStatus.fuelReceipt,    icon: Fuel,           label: 'Fuel receipt',
                                onClick: () => setViewing({ ...trip }) },
                              { status: trip.uploadStatus.damageIncident, icon: AlertTriangle,  label: 'Damage / incident',
                                onClick: () => setViewing({ ...trip }) },
                            ]} />
                          )}
                        </td>
                        <td className="px-3 py-2 text-end">
                          <TripStatusBadge status={trip.status} locked={trip.locked} size="sm" />
                        </td>
                        <td className="px-3 py-2 text-end" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="gap-1.5"
                            onClick={() => setViewing({ ...trip })}
                            aria-label={`Edit ${trip.code}`}>
                            <Pencil className="h-3.5 w-3.5" /> Edit
                          </Button>
                        </td>
                      </tr>,
                    );
                  }
                  return rows;
                })()}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 py-3 border-t border-border">
            <span className="text-sm text-muted-foreground">
              Showing <b className="text-foreground">{(page - 1) * rowsPerPage + 1}</b>
              {' to '}<b className="text-foreground">{Math.min(page * rowsPerPage, total)}</b>
              {' of '}<b className="text-foreground">{total.toLocaleString('en-US')}</b> trips
            </span>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground" htmlFor="rpp">Rows</label>
              <select id="rpp" value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        </>
        )}{/* end viewMode list branch */}
      </main>

      {/* Quick Log sheet */}
      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('trips.quickLog')}</SheetTitle>
            <SheetDescription>{t('trips.quickLogDescription')}</SheetDescription>
          </SheetHeader>
          <QuickLogForm onCreated={() => setCreating(false)} onCancel={() => setCreating(false)} />
        </SheetContent>
      </Sheet>

      {/* Detail sheet */}
      <Sheet open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{viewing?.code}</SheetTitle>
            <SheetDescription className="font-mono text-xs">
              {viewing && formatTripDate(viewing.tripDate)} · {viewing && t(`trips.tripType.${viewing.tripType}`)}
            </SheetDescription>
          </SheetHeader>
          <TripDetailSheet initial={viewing} onClose={() => setViewing(null)} />
        </SheetContent>
      </Sheet>
      <LockMonthDialog open={lockingMonth} onClose={() => setLockingMonth(false)} />

      {/* PR-MAY19-CALENDAR-DAYDRILL — opened from TripCalendar's onPickDay.
          Clicking a vehicle row in the drill drops into the list view
          pre-filtered to that vehicle on that single date, so the
          operator sees the per-vehicle/day rows + docs icons + status
          (the reconcile-at-a-glance workflow). */}
      <TripDayDrillSheet
        date={drillDate}
        onClose={() => setDrillDate(null)}
        onPickVehicle={(vehId, ymd) => {
          setDrillDate(null);
          setViewMode('list');
          setVehicleFilter(vehId);
          setDateFilter('custom');
          setCustomFrom(ymd); setCustomTo(ymd);
          setPage(1);
        }}
      />

      {/* PR-DEL-TRIPS-DRIVERS MAY 11 — bulk delete drawer (super_admin only) */}
      <DeleteConfirmDrawer
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        entityKind="trips"
        label={`${selected.size} selected trip(s)`}
        forceWarning="Cascade-deletes photos, OCR, wasika, collections, advances, fuel + water wallet fill transactions. Wallet balance NOT auto-refunded."
        initialCounts={{ selected: selected.size }}
        performDelete={performBulkDelete}
        onDeleted={() => {
          refetch();
          // Everything derived from trips has to be told, not just this table.
          void qcTrips.invalidateQueries();
        }}
      />
    </div>
  );
}


// PR-MAY19-TRIPS-LIST-OVERHAUL — sortable column header. Click cycles
// asc → desc → off so the operator can always get back to the default
// tripDate-desc + createdAt-desc tuple without picking a status.
function SortableTh({
  label, sortKey, currentKey, currentDir, onSort, align, extraClass,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey | undefined;
  currentDir: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
  align?: 'start' | 'end';
  extraClass?: string;
}) {
  const active = currentKey === sortKey;
  const Icon = !active ? ArrowUpDown : currentDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      className={`px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground select-none cursor-pointer hover:text-foreground transition-colors text-${align ?? 'start'} ${extraClass ?? ''}`}
      onClick={() => onSort(sortKey)}
      data-trips-sort={sortKey}
      data-trips-sort-active={active ? currentDir : 'off'}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`h-3 w-3 ${active ? 'text-foreground' : 'opacity-50'}`} />
      </span>
    </th>
  );
}

/**
 * Helper rendered next to a trip row when OCR needs attention.
 * Use:  {needsOcrAttention(trip) && <OcrIndicator status={trip.ocr_status} />}
 */
export function OcrIndicator({ status }: { status: string }) {
  if (!status || status === 'none' || status === 'extracted' || status === 'confirmed') return null;
  const color = status === 'failed' ? 'text-red-500'
              : status === 'review_needed' ? 'text-amber-500'
              : 'text-blue-500';
  return <ScanLine className={`h-4 w-4 ${color}`} aria-label={status} />;
}

export function needsOcrAttention(trip: { ocr_status?: string }) {
  return ['pending', 'processing', 'review_needed', 'failed'].includes(trip.ocr_status || '');
}
